import productApi from "@api/products" //eslint-disable-line
import Vue from 'vue'
import { productFormInfo } from '@components/products/constants/constants'
import { uniqBy } from 'lodash'
import { stateToAPIRequestTransformer } from '@store/products/helpers'

export default {
    namespaced: true,
    state: {
        loading: {
            product: false,
            productList: false,
            productDraftSave: false,
            productSave: false,
            categorySave: false,
            usefulResourceSave: false,
        },
        errors: {
            product: null,
            productList: null,
            productDraftSave: null,
            productSave: null,
            categorySave: null,
        },
        productList: [],
        categories: [],
        featuresByCategory: [],
        pricingLevels: [],
        industries: [],
        companySizes: [],
        product: {
            images: [],
            categories: [],
            industries: [],
            company_sizes: [],
            prices: [],
            pricing_model: [],
            free_trial_available: 0,
            features: [],
            integrations: [],
            custom_integrations: [],
            standard_integrations: [],
            useful_resources: [],
        },
        productHistory: [],
        wizardStartOver: true,
        desiredAccountPlan: 'free',
        hasSucceededPlanUpgrade: false,
    },
    getters: {
        product: ({ product }) => product,
        category: ({ category }) => category,
        categories: state => state.categories,
        featuresByCategory: state => state.featuresByCategory,
        pricingLevels: state => state.pricingLevels,
        industries: state => state.industries,
        companySizes: state => state.companySizes,
        productList: state => state.productList,
        productHistory: state => state.productHistory,
        productByUuid: state => uuid => state.productList.find(product => product.uuid === uuid),
        getLoadingState: state => name => state.loading[name],
        getErrorState: state => name => state.errors[name],
        wizardStartOver: state => state.wizardStartOver,
        desiredAccountPlan: state => state.desiredAccountPlan,
        hasSucceededPlanUpgrade: state => state.hasSucceededPlanUpgrade,
        stepStatuses(state) {
            return {
                product: {
                    started: true,
                    complete: !!state.product.name && !!state.product.short_description
                        && productFormInfo.regex.test(state.product.url),
                },
                categories: {
                    started: !!state.product.name && !!state.product.short_description
                        && productFormInfo.regex.test(state.product.url),
                    complete: state.product.categories && !!state.product.categories.length,
                },
                media: {
                    started: state.product.categories && !!state.product.categories.length,
                    complete: state.product.images && !!state.product.images.length,
                },
                features: {
                    started: state.product.images && !!state.product.images.length,
                    complete: state.product.features && !!state.product.features.length,
                },
                summary: {
                    started: false,
                    complete: false,
                },
            }
        },
    },
    mutations: {
        setError(state, error) {
            Vue.set(state.errors, error.name, error.value)
        },
        setLoadingState(state, loading) {
            Vue.set(state.loading, loading.name, loading.value)
        },
        setProduct(state, data) {
            Vue.set(state, 'product', data.data)
        },
        setProductFromAdd(state, product) {
            Vue.set(state, 'product', product)
        },
        clearProduct(state) {
            Vue.set(state, 'product', {
                images: [],
                categories: [],
                features: [],
                integrations: [],
                custom_integrations: [],
                standard_integrations: [],
                industries: [],
                company_sizes: [],
                prices: [],
                pricing_model: [],
                free_trial_available: 0,
                useful_resources: [],
            })
        },
        setProductList(state, products) {
            Vue.set(state, 'productList', products)
        },
        deleteProduct(state, uuid) {
            const productIndex = state.productList.findIndex(product => product.uuid === uuid)
            state.productList.splice(productIndex, 1)
        },
        addProduct(state, product) {
            state.productList.push(product)
        },
        updateProductProperty(state, payload) {
            Vue.set(state.product, payload.property, payload.value)
        },
        setCategories(state, categories) {
            Vue.set(state, 'categories', categories)
        },
        setFeaturesByCategory(state, featuresByCategory) {
            Vue.set(state, 'featuresByCategory', featuresByCategory)
        },
        setIndustries(state, industries) {
            Vue.set(state, 'industries', industries)
        },
        setPricingLevels(state, pricingLevels) {
            Vue.set(state, 'pricingLevels', pricingLevels)
        },
        setCompanySizes(state, companySizes) {
            Vue.set(state, 'companySizes', companySizes)
        },
        addProductImage(state, productImage) {
            state.product.images.push(productImage)
        },
        addUsefulResource(state, usefulResource) {
            state.product.useful_resources.push(usefulResource)
        },
        setProductHeaderImage(state, headerImage) {
            Vue.set(state.product, 'header_image', headerImage)
        },
        deleteProductImage(state, uuid) {
            const imageIndex = state.product.images.findIndex(image => image.uuid === uuid)
            state.product.images.splice(imageIndex, 1)
        },
        deleteUsefulResource(state, usefulResourceUuid) {
            const resourceIndex = state.product.useful_resources.findIndex(res => res.uuid === usefulResourceUuid)
            state.product.useful_resources.splice(resourceIndex, 1)
        },
        setCategory(state, data) {
            Vue.set(state, 'category', data)
        },
        updateCategoryProperty(state, payload) {
            Vue.set(state.category, payload.property, payload.value)
        },
        updateProductScreenshotCaption(state, payload) {
            const imageIndex = state.product.images.findIndex(img => img.uuid === payload.image.uuid)
            Vue.set(state.product.images[imageIndex], 'caption', payload.image.caption)
        },
        updateUsefulResource(state, payload) {
            const usefulResourceIndex = state.product.useful_resources
                .findIndex(resource => resource.uuid === payload.uuid)
            Vue.set(state.product.useful_resources, usefulResourceIndex, payload)
        },
        setProductHistory(state, history) {
            Vue.set(state, 'productHistory', history)
        },
        setDesiredAccountPlan(state, plan) {
            Vue.set(state, 'desiredAccountPlan', plan)
        },
        setHasSucceededPlanUpgrade(state, value) {
            Vue.set(state, 'hasSucceededPlanUpgrade', value)
        },
    },
    actions: {
        getProduct({ commit, state }, { uuid, params = {} }) {
            if (!uuid) {
                commit('clearProduct')
                return Promise.resolve(state.product)
            }

            return productApi
                .getProduct(uuid, params)
                .then(response => {
                    // axiosV2 response structure: response.data.data contains the product
                    // Match productsReview pattern but wrap for setProduct mutation that expects data.data
                    const productData = response.data?.data || response.data || response
                    commit('setProduct', { data: productData })
                    return response
                })
                .catch(error => {
                    console.error('Error in getProduct action:', error, error.response?.data)
                    commit('setError', { name: 'product', value: true })
                    throw error
                })
        },
        getProductHistory({ commit }, uuid) {
            // Necessary to reset productHistory to ensure the filters in productHistoryModal
            // have the correct options when viewing from product list page
            commit('setProductHistory', [])
            commit('setLoadingState', { name: 'productHistory', value: true })

            return productApi
                .getProductHistory(uuid)
                .then(response => {
                    const history = response.data.data
                    commit('setProductHistory', history)
                })
                .catch(() => {
                    commit('setError', { name: 'productHistory', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'productHistory', value: false })
                })
        },
        setProductList({ commit }, params = {}) {
            commit('setLoadingState', { name: 'productList', value: true })

            return productApi
                .getProducts(params)
                .then(response => {
                    const { data } = response
                    commit('setProductList', data.data)

                    return data.meta
                })
                .catch(() => {
                    commit('setError', {
                        name: 'productList',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'productList', value: false })
                })
        },
        getCategory({ commit }, uuid) {
            commit('setLoadingState', { name: 'category', value: true })

            return productApi
                .getCategory(uuid)
                .then(response => {
                    const { data } = response.data
                    commit('setCategory', data)
                })
                .catch(() => {
                    commit('setError', { name: 'category', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'category', value: false })
                })
        },
        updateCategoryProperty({ commit }, payload) {
            commit('updateCategoryProperty', payload)
        },
        setCategories({ commit }, params = {}) {
            commit('setLoadingState', { name: 'categories', value: true })

            productApi
                .getCategories(params)
                .then(response => {
                    const { data } = response.data

                    commit('setCategories', data)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'categories',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'categories', value: false })
                })
        },
        setFeaturesByCategory({ commit, state }, categoryUuids = null) {
            commit('setLoadingState', { name: 'features', value: true })
            let categories = categoryUuids
            if (!categoryUuids) {
                categories = state.product.categories.map(category => category.uuid)
            }

            productApi
                .getFeaturesByCategory({ category_uuids: categories })
                .then(response => {
                    const { data } = response.data

                    commit('setFeaturesByCategory', data)
                })
                .catch(() => {
                    commit('setError', { name: 'features', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'features', value: false })
                })
        },
        setIndustries({ commit }) {
            commit('setLoadingState', { name: 'industries', value: true })

            return productApi
                .getIndustries()
                .then(response => {
                    const { data } = response.data

                    commit('setIndustries', data)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'industries',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'industries', value: false })
                })
        },
        setCompanySizes({ commit }) {
            commit('setLoadingState', { name: 'companySizes', value: true })

            productApi
                .getCompanySizes()
                .then(response => {
                    const { data } = response.data

                    commit('setCompanySizes', data)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'companySizes',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'companySizes', value: false })
                })
        },
        setPricingLevels({ commit }) {
            commit('setLoadingState', { name: 'pricingLevels', value: true })

            productApi
                .getPricingLevels()
                .then(response => {
                    const { data } = response.data

                    commit('setPricingLevels', data)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'pricingLevels',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'pricingLevels', value: false })
                })
        },
        updateCategory({ commit, state }) {
            commit('setLoadingState', { name: 'categorySave', value: true })
            commit('setError', { name: 'categorySave', value: false })

            return productApi
                .updateCategory(state.category.uuid, state.category)
                .catch(() => {
                    commit('setError', {
                        name: 'categorySave',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'categorySave', value: false })
                })
        },
        addCategory({ commit }, categoryPayload) {
            commit('setLoadingState', { name: 'categorySave', value: true })
            commit('setError', { name: 'categorySave', value: null })

            return productApi
                .addCategory(categoryPayload)
                .catch(() => {
                    commit('setError', { name: 'categorySave', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'categorySave', value: false })
                })
        },
        clearCategory({ commit }) {
            commit('setCategory', {
                parent_category_id: null,
                name: '',
                description: '',
                slug: '',
                icp_bidding_status: false,
                traction_insights_status: false,
                product_catalog_status: false,
            })
        },
        addProduct({ commit, state }, { productAdditions, params }) {
            commit('setLoadingState', { name: 'productSave', value: true })
            commit('setError', { name: 'productSave', value: false })

            const productCopy = stateToAPIRequestTransformer(state.product, productAdditions);

            return productApi
                .addProduct(productCopy, params)
                .then(response => {
                    const product = {
                        ...response.data.data,
                    }
                    commit('addProduct', product)
                    commit('setProductFromAdd', product)
                })
                .catch(() => {
                    commit('setError', { name: 'productSave', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'productSave', value: false })
                })
        },
        updateProduct({ commit, state }) {
            if (state.product.draft) {
                commit('setLoadingState', { name: 'productDraftSave', value: true })
                commit('setError', { name: 'productDraftSave', value: false })
            } else {
                commit('setLoadingState', { name: 'productSave', value: true })
                commit('setError', { name: 'productSave', value: false })
            }
            const targetingSetCategoryIds = state.product.targeting_sets
                .filter(ts => ts.is_active)
                .map(ts => ts.category_id)
            const targetingSetCategories = state.categories
                .map(cat => cat.child_categories)
                .flat()
                .filter(cat => targetingSetCategoryIds.includes(cat.id))
            const productCategories = uniqBy([ ...targetingSetCategories, ...state.product.categories ], 'uuid')

            const productCopy = stateToAPIRequestTransformer(state.product, {
                categories: productCategories,
            })

            return productApi
                .updateProduct(state.product.uuid, productCopy)
                .then(resp => resp.data.data)
                .catch(response => {
                    commit('setError', {
                        name: 'productSave',
                        value: true,
                    })
                    return response.response.data.errors
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'productDraftSave', value: false })
                    commit('setLoadingState', { name: 'productSave', value: false })
                })
        },
        updateProductImagesSortOrder({ state }, images) { // eslint-disable-line
            return productApi.updateProductImagesSortOrder(images)
        },
        updateUsefulResource({ commit }, { usefulResource, replacedImage = null }) { // eslint-disable-line
            // If image was replaced, DB has already been updated, but need to replace the old image in the store
            if (replacedImage) {
                commit('updateUsefulResource', usefulResource)
                commit('deleteProductImage', replacedImage.uuid)
                commit('addProductImage', usefulResource.image)
            } else {
                commit('setLoadingState', { name: 'usefulResourceSave', value: true })
                const resourceData = {
                    title: usefulResource.title,
                    description: usefulResource.description,
                    url: usefulResource.url,
                    is_sponsored: usefulResource.is_sponsored,
                }
                return productApi.updateUsefulResource(usefulResource.uuid, resourceData)
                    .then(() => {
                        commit('updateUsefulResource', usefulResource)
                    })
                    .finally(() => {
                        commit('setLoadingState', { name: 'usefulResourceSave', value: false })
                    })
            }
        },
        updateUsefulResources({ commit }, usefulResources) {
            commit('updateProductProperty', { property: 'useful_resources', value: usefulResources })
        },
        clearError({ commit }, name) {
            commit('setError', {
                name,
                value: null,
            })
        },
        updateProductProperty({ commit }, payload) {
            commit('updateProductProperty', payload)
        },
        initializeCategories({ dispatch }, params = {}) {
            dispatch('setCategories', params)
        },
        initializeFeaturesByCategory({ dispatch }, categoryUuids = null) {
            dispatch('setFeaturesByCategory', categoryUuids)
        },
        initializeIndustries({ dispatch }) {
            return dispatch('setIndustries')
        },
        initializeCompanySizes({ dispatch }) {
            dispatch('setCompanySizes')
        },
        initializePricingLevels({ dispatch }) {
            dispatch('setPricingLevels')
        },
        initializeProductImages({ commit }) {
            commit('updateProductProperty', { property: 'images', value: [] })
        },
        clearCurrentProduct({ commit }) {
            commit('clearProduct')
        },
        deleteProduct({ commit }, uuid) {
            commit('setLoadingState', { name: 'productDelete', value: true })

            return productApi.deleteProduct(uuid)
                .then(() => {
                    commit('deleteProduct', uuid)
                }).catch(() => {
                    commit('setError', {
                        name: 'productDelete',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'productDelete', value: false })
                })
        },
        addProductImage({ commit }, payload) {
            commit('addProductImage', payload)
        },
        setProductHeaderImage({ commit }, payload) {
            commit('setProductHeaderImage', payload)
        },
        addUsefulResource({ commit }, payload) {
            commit('addUsefulResource', payload)
            commit('addProductImage', payload.image)
        },
        deleteProductImage({ commit }, payload) {
            commit('setLoadingState', { name: 'productImageDelete', value: true })
            return productApi.deleteProductImage(payload.productUuid, payload.imageUuid)
                .then(() => {
                    commit('deleteProductImage', payload.imageUuid)
                }).catch(() => {
                    commit('setError', {
                        name: 'productImageDelete',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'productImageDelete', value: false })
                })
        },
        deleteProductImageFromAdd({ commit }, uuid) {
            commit('deleteProductImage', uuid)
        },
        deleteUsefulResource({ commit }, usefulResource) {
            commit('setLoadingState', { name: 'usefulResourceDelete', value: true })
            return productApi.deleteUsefulResource(usefulResource.uuid)
                .then(() => {
                    commit('deleteUsefulResource', usefulResource.uuid)
                    commit('deleteProductImage', usefulResource.image.uuid)
                }).catch(() => {
                    commit('setError', {
                        name: 'usefulResourceDelete',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'usefulResourceDelete', value: false })
                })
        },
        updateProductScreenshotCaption({ commit }, payload) {
            const { image, product_uuid } = payload //eslint-disable-line
            return productApi
                .updateProductScreenshotCaption(image.uuid, { caption: image.caption, product_uuid }) //eslint-disable-line
                .then(() => {
                    commit('updateProductScreenshotCaption', payload)
                })
        },
        setDesiredAccountPlan({ commit }, payload) {
            commit('setDesiredAccountPlan', payload)
        },
        setHasSucceededPlanUpgrade({ commit }, value) {
            commit('setHasSucceededPlanUpgrade', value)
        },
    },
}
